import classNames from 'classnames';
import type { ReactNode } from 'react';
import { handleTabListKeyDown, resolveTabStop } from '@sorrem/ui';
import styles from './TextTabs.module.scss';

export interface TextTab<T extends string> {
    readonly id: T;
    readonly label: ReactNode;
    /** A key hint shown after the label, e.g. `P`. */
    readonly keyLabel?: string;
    /** Needs attention while another tab shows: glows. */
    readonly isFlashing?: boolean;
    /** Shown but not selectable. */
    readonly disabled?: boolean;
    /** The id of the panel this tab shows, for `aria-controls`. */
    readonly controls?: string;
}

interface TextTabsProps<T extends string> {
    readonly tabs: readonly TextTab<T>[];
    /** The tab showing; null when none is (a view toggled off). */
    readonly activeTab: T | null;
    readonly onTabChange: (id: T) => void;
    readonly ariaLabel: string;
    readonly className?: string;
}

/**
 * Plain shadowed text tabs, the active one underlined in gold: the party
 * panel's Party and Chat tabs, and anywhere a light switch between two or
 * three views reads better than framed tabs. Tab reaches the selected tab;
 * while a tab has focus the arrows, Home and End move between tabs (shared
 * with the ui TabStrip).
 */
export function TextTabs<T extends string>({ tabs, activeTab, onTabChange, ariaLabel, className }: TextTabsProps<T>): JSX.Element {
    const tabStop = resolveTabStop(tabs, activeTab);
    return (
        <div className={classNames(styles.textTabs, className)} role="tablist" aria-label={ariaLabel} onKeyDown={handleTabListKeyDown}>
            {tabs.map((tab) => (
                <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    aria-controls={tab.controls}
                    tabIndex={tab.id === tabStop ? 0 : -1}
                    disabled={tab.disabled}
                    className={classNames(styles.tab, activeTab === tab.id && styles.active, tab.isFlashing && styles.flashing)}
                    onClick={(event) => {
                        // Hand the keys back to the page, so tab hotkeys keep working.
                        event.currentTarget.blur();
                        onTabChange(tab.id);
                    }}
                >
                    {tab.label}
                    {tab.keyLabel !== undefined && tab.keyLabel !== '' && <span className={styles.key}>({tab.keyLabel})</span>}
                </button>
            ))}
        </div>
    );
}
